/**
 * GraphCanvas — React Flow rendering of a session's directed multigraph.
 *
 * Replaces an earlier Cytoscape implementation. React Flow gives us proper
 * React components for nodes (see TabNode.tsx) and customisable edges, plus
 * built-in pan/zoom, minimap, controls, and selection — all stylable from
 * the same Tailwind/shadcn vocabulary the rest of the SPA uses.
 *
 * Supports:
 *   - Cycles, multi-parent, multi-child (anything dagre/elk can lay out).
 *   - Two layout backends: dagre (fast, default ≤500 nodes) and elkjs
 *     (better for dense graphs, default >500 nodes). User can override
 *     via the toolbar Tabs.
 *   - Subtree-on-demand expansion for graphs above SUBTREE_FILTER_THRESHOLD.
 *     Tapping a hidden-neighbour node reveals its N-hop neighbourhood.
 *   - Node click → Zustand setSelectedNodeId → Sheet drawer (in the parent).
 *   - Edge color coding by EdgeType (OPENED_TAB blue, NAVIGATED gray,
 *     HISTORY_PUSHSTATE light-gray, RESTORED dashed light).
 *   - "Showing X of Y nodes" hint when subtree filter is active.
 *
 * PNG export lives in exportPng.ts and uses html-to-image against the
 * `.react-flow__viewport` element.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  MarkerType,
  type Edge,
  type Node,
  type OnNodesChange,
  applyNodeChanges,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { Graph } from "@/gen/tabgraph/v1/graph_pb";
import { EdgeType } from "@/gen/tabgraph/v1/graph_pb";
import { useUIStore } from "@/stores/uiStore";
import { TabNode, type TabNodeData } from "./TabNode";
import {
  chooseLayout,
  collectSubtreeIds,
  DEFAULT_HOP_RADIUS,
  SUBTREE_FILTER_THRESHOLD,
  layoutWithDagre,
  layoutWithElk,
} from "./layouts";

const NODE_TYPES = { tab: TabNode };

const EDGE_COLORS: Record<EdgeType, string> = {
  [EdgeType.UNSPECIFIED]: "#94a3b8",
  [EdgeType.OPENED_TAB]: "#3b82f6", // blue
  [EdgeType.NAVIGATED]: "#64748b", // slate
  [EdgeType.HISTORY_PUSHSTATE]: "#94a3b8", // light slate
  [EdgeType.RESTORED]: "#cbd5e1", // very light (dashed)
};

interface GraphCanvasProps {
  graph: Graph;
  className?: string;
}

export function GraphCanvas({ graph, className }: GraphCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    selectedNodeId,
    setSelectedNodeId,
    graphLayout,
    graphLayoutExplicit,
    setGraphLayout,
    subtreeFilterEnabled,
    setSubtreeFilterEnabled,
  } = useUIStore();

  // -------------------------------------------------------------------------
  // Build the raw React Flow node and edge arrays from the proto.
  // -------------------------------------------------------------------------
  const { allNodes, allEdges } = useMemo(() => {
    const ns: Node<TabNodeData>[] = graph.nodes.map((n) => ({
      id: n.id,
      type: "tab",
      position: { x: 0, y: 0 }, // overwritten by layout
      data: {
        title: n.page?.title ?? "",
        canonicalUrl: n.canonicalUrl,
        faviconUrl: n.page?.faviconUrl ?? "",
        urlHost: n.urlHost,
        visitCount: n.visitCount,
      },
    }));

    const es: Edge[] = [];
    for (const e of graph.edges) {
      if (!e.fromNodeId) continue; // root edges aren't drawn
      const color = EDGE_COLORS[e.type] ?? EDGE_COLORS[EdgeType.UNSPECIFIED];
      es.push({
        id: e.id,
        source: e.fromNodeId,
        target: e.toNodeId,
        type: "default",
        animated: false,
        label: e.traversalCount > 1 ? String(e.traversalCount) : undefined,
        labelBgPadding: [4, 2],
        labelBgStyle: { fill: "#fff", fillOpacity: 0.85 },
        labelStyle: { fontSize: 10, fill: "#64748b" },
        style: {
          stroke: color,
          strokeWidth: 1.5,
          strokeDasharray: e.type === EdgeType.RESTORED ? "4 4" : undefined,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color,
          width: 16,
          height: 16,
        },
      });
    }
    return { allNodes: ns, allEdges: es };
  }, [graph]);

  const totalNodeCount = allNodes.length;

  // -------------------------------------------------------------------------
  // Auto-select layout based on graph size (only when user hasn't toggled).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (graphLayoutExplicit) return;
    const recommended = chooseLayout(totalNodeCount) === "elk" ? "force" : "hierarchical";
    if (recommended !== graphLayout) {
      setGraphLayout(recommended, false);
    }
  }, [totalNodeCount, graphLayout, graphLayoutExplicit, setGraphLayout]);

  // Auto-enable subtree filter for large graphs on first load.
  const didAutoFilterRef = useRef(false);
  useEffect(() => {
    if (!didAutoFilterRef.current && totalNodeCount > SUBTREE_FILTER_THRESHOLD) {
      setSubtreeFilterEnabled(true);
      didAutoFilterRef.current = true;
    }
  }, [totalNodeCount, setSubtreeFilterEnabled]);

  // -------------------------------------------------------------------------
  // Determine focus node for subtree expansion.
  // -------------------------------------------------------------------------
  const getFocusNodeId = useCallback((): string | null => {
    if (selectedNodeId) return selectedNodeId;
    const lastRoot = [...graph.edges].reverse().find((e) => !e.fromNodeId);
    if (lastRoot) return lastRoot.toNodeId;
    return graph.nodes[0]?.id ?? null;
  }, [selectedNodeId, graph]);

  // -------------------------------------------------------------------------
  // Track which node IDs are currently visible (subtree filter).
  // -------------------------------------------------------------------------
  const [visibleIds, setVisibleIds] = useState<Set<string> | null>(null);

  // Recompute the visible-ids set when the graph or filter toggle changes.
  useEffect(() => {
    if (!subtreeFilterEnabled || totalNodeCount <= SUBTREE_FILTER_THRESHOLD) {
      setVisibleIds(null);
      return;
    }
    const focus = getFocusNodeId();
    if (!focus) {
      setVisibleIds(null);
      return;
    }
    setVisibleIds(collectSubtreeIds(allEdges, focus, DEFAULT_HOP_RADIUS));
    // We intentionally don't depend on selectedNodeId — selection-driven
    // expansion happens in the node-click handler below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtreeFilterEnabled, totalNodeCount, allEdges]);

  // -------------------------------------------------------------------------
  // Apply layout to the visible slice.
  // -------------------------------------------------------------------------
  const [laidOutNodes, setLaidOutNodes] = useState<Node<TabNodeData>[]>([]);
  const [laidOutEdges, setLaidOutEdges] = useState<Edge[]>([]);

  useEffect(() => {
    let cancelled = false;

    const visibleSlice = visibleIds
      ? {
          nodes: allNodes.filter((n) => visibleIds.has(n.id)),
          edges: allEdges.filter(
            (e) => visibleIds.has(e.source) && visibleIds.has(e.target),
          ),
        }
      : { nodes: allNodes, edges: allEdges };

    const run = async () => {
      try {
        const laid =
          graphLayout === "force"
            ? await layoutWithElk(visibleSlice.nodes, visibleSlice.edges)
            : layoutWithDagre(visibleSlice.nodes, visibleSlice.edges);
        if (cancelled) return;
        setLaidOutNodes(laid as Node<TabNodeData>[]);
        setLaidOutEdges(visibleSlice.edges);
      } catch (err) {
        // elk can throw on degenerate inputs — fall back to dagre.
        if (cancelled) return;
        console.warn("layout failed, falling back to dagre:", err);
        const fallback = layoutWithDagre(visibleSlice.nodes, visibleSlice.edges);
        setLaidOutNodes(fallback as Node<TabNodeData>[]);
        setLaidOutEdges(visibleSlice.edges);
      }
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [allNodes, allEdges, visibleIds, graphLayout]);

  // -------------------------------------------------------------------------
  // Selection state — annotate each rendered node with `selected` so React
  // Flow can render its selected style and our TabNode picks it up too.
  // -------------------------------------------------------------------------
  const displayNodes = useMemo(
    () =>
      laidOutNodes.map((n) => ({
        ...n,
        selected: n.id === selectedNodeId,
      })),
    [laidOutNodes, selectedNodeId],
  );

  // -------------------------------------------------------------------------
  // Handlers — node click selects + reveals neighbourhood; background click
  // deselects. Position changes from React Flow's drag are absorbed.
  // -------------------------------------------------------------------------
  const onNodeClick = useCallback(
    (_e: unknown, node: Node) => {
      setSelectedNodeId(node.id);
      if (subtreeFilterEnabled && visibleIds) {
        const reach = collectSubtreeIds(allEdges, node.id, DEFAULT_HOP_RADIUS);
        let grew = false;
        for (const id of reach) {
          if (!visibleIds.has(id)) {
            grew = true;
            break;
          }
        }
        if (grew) {
          const next = new Set(visibleIds);
          for (const id of reach) next.add(id);
          setVisibleIds(next);
        }
      }
    },
    [setSelectedNodeId, subtreeFilterEnabled, visibleIds, allEdges],
  );

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, [setSelectedNodeId]);

  // Double-click on a node opens its URL in a new tab. Mirrors standard
  // graph-viewer convention (single click selects, double click activates).
  // The hover-revealed external-link icon on TabNode gives users a
  // discoverable affordance for the same action.
  const onNodeDoubleClick = useCallback((_e: unknown, node: Node) => {
    const data = node.data as TabNodeData | undefined;
    const url = data?.canonicalUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const onNodesChange: OnNodesChange = useCallback((changes) => {
    setLaidOutNodes((curr) => applyNodeChanges(changes, curr) as Node<TabNodeData>[]);
  }, []);

  const visibleNodeCount = displayNodes.length;
  const hidden = totalNodeCount - visibleNodeCount;

  return (
    <div ref={containerRef} className={className} data-graph-canvas>
      <ReactFlow
        nodes={displayNodes}
        edges={laidOutEdges}
        nodeTypes={NODE_TYPES}
        onNodesChange={onNodesChange}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onPaneClick={onPaneClick}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ type: "default" }}
      >
        <Background gap={20} size={1} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable nodeStrokeWidth={2} />
      </ReactFlow>
      {hidden > 0 && (
        <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-md bg-background/90 px-3 py-1 text-xs text-muted-foreground shadow border border-border">
          Showing {visibleNodeCount} of {totalNodeCount} nodes · click a node to
          reveal its neighbours
        </div>
      )}
    </div>
  );
}
